import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trackOrder, trackOrderBySlug } from '../lib/api';
import { getPaymentLabel, getPaymentReferenceId, getShippingMethodDescription } from '../lib/orderDisplay';
import MapPreview from '../components/MapPreview';
import './TrackOrderPage.css';

/* ── Icons (same style used on OrderStatusPage / OrderConfirmationPage) ── */
function CheckCircleIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12.5 11 15.5 16 9" />
    </svg>
  );
}
function XCircleIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/* ── Status timeline (identical steps/logic to OrderStatusPage) ── */
const STEPS = [
  { key: 'confirmed',        label: 'Confirmed',        sub: 'Order placed' },
  { key: 'processing',       label: 'Processing',       sub: 'Getting your plant ready' },
  { key: 'shipped',          label: 'Shipped',          sub: 'On its way to you' },
  { key: 'out_for_delivery', label: 'Out for Delivery', sub: 'Almost there!' },
  { key: 'delivered',        label: 'Delivered',        sub: 'Arrived' },
];

function StatusTimeline({ status, deliveredOn }) {
  if (status === 'cancelled') {
    return (
      <div className="tro-timeline tro-timeline--cancelled">
        <div className="tro-timeline__cancel-icon"><XCircleIcon /></div>
        <div>
          <p className="tro-timeline__cancel-title">Order cancelled</p>
          <p className="tro-timeline__cancel-sub">This order will not be processed further.</p>
        </div>
      </div>
    );
  }

  const currentIdx = STEPS.findIndex(s => s.key === status);
  const activeIdx = currentIdx === -1 ? 0 : currentIdx;

  return (
    <div className="tro-timeline">
      {STEPS.map((step, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={step.key} className={`tro-step${done ? ' tro-step--done' : ''}${active ? ' tro-step--active' : ''}`}>
            <div className="tro-step__rail">
              <span className="tro-step__dot">{done ? '✓' : i + 1}</span>
              {i < STEPS.length - 1 && <span className="tro-step__line" />}
            </div>
            <div className="tro-step__body">
              <p className="tro-step__label">{step.label}</p>
              <p className="tro-step__sub">{step.key === 'delivered' && deliveredOn ? deliveredOn : step.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SkeletonBlock({ className }) {
  return <div className={`tro-skel ${className || ''}`} />;
}

/* ── Full-page skeleton shown while the one-click email link loads ── */
function TrackOrderSkeleton() {
  return (
    <div className="tro-page-outer">
      <main className="tro-main">
        <SkeletonBlock className="tro-skel--back" />
        <div className="tro-inner">
          <div className="tro-left">
            <div className="tro-hero">
              <SkeletonBlock className="tro-skel--circle" />
              <SkeletonBlock className="tro-skel--line-sm" />
              <SkeletonBlock className="tro-skel--line-lg" />
              <SkeletonBlock className="tro-skel--line-sm" />
            </div>

            <section className="tro-card">
              <SkeletonBlock className="tro-skel--line-md" />
              <div className="tro-skel-timeline">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="tro-skel-step">
                    <SkeletonBlock className="tro-skel--dot" />
                    <SkeletonBlock className="tro-skel--line-xs" />
                  </div>
                ))}
              </div>
            </section>

            <section className="tro-card">
              <SkeletonBlock className="tro-skel--line-md" />
              <SkeletonBlock className="tro-skel--line-lg" />
              <SkeletonBlock className="tro-skel--line-lg" />
              <SkeletonBlock className="tro-skel--line-md" />
            </section>
          </div>

          <aside className="tro-right">
            <div className="tro-summary">
              {[0, 1].map(i => (
                <div key={i} className="tro-skel-summary-item">
                  <SkeletonBlock className="tro-skel--thumb" />
                  <div className="tro-skel-summary-item__body">
                    <SkeletonBlock className="tro-skel--line-md" />
                    <SkeletonBlock className="tro-skel--line-xs" />
                  </div>
                </div>
              ))}
              <div className="tro-divider" />
              <SkeletonBlock className="tro-skel--line-md" />
              <SkeletonBlock className="tro-skel--line-md" />
              <SkeletonBlock className="tro-skel--line-md" />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default function TrackOrderPage() {
  const { slug } = useParams(); // present when opened from the emailed one-click tracking link
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState('');
  const [email, setEmail]     = useState('');
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState(false);

  // /track-order/:slug — the link from order-notification emails. No form,
  // no email needed: the slug itself proves ownership, so load the order
  // status straight away.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    trackOrderBySlug(slug)
      .then(data => { if (!cancelled) setOrder(data); })
      .catch(err => { if (!cancelled) setError(err.message || 'This tracking link is invalid or has expired.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) {
      setError('Please enter both your order ID and email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await trackOrder(orderId.trim(), email.trim());
      setOrder(data);
    } catch (err) {
      setOrder(null);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackAnother = () => {
    setOrder(null);
    setOrderId('');
    setEmail('');
    setError('');
    if (slug) navigate('/track-order');
  };

  const handleCopyTracking = () => {
    if (!order?.tracking) return;
    navigator.clipboard?.writeText(order.tracking);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  /* ── Any order lookup in flight (one-click email link, or manual search) ── */
  if (loading) {
    return <TrackOrderSkeleton />;
  }

  /* ── Emailed one-click tracking link came back with no match ── */
  if (slug && error) {
    return (
      <div className="tro-page-outer">
        <div className="tro-search-panel">
          <div className="tro-search-wrap">
            <div className="tro-search-icon"><SearchIcon /></div>
            <h1 className="tro-search-title">Tracking link not found</h1>
            <p className="tro-search-subtitle">{error}</p>
            <Link to="/track-order" className="tro-btn tro-btn--primary">
              Track using order ID
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ── Search form (no order loaded yet) ── */
  if (!order) {
    return (
      <div className="tro-page-outer">
        <div className="tro-search-panel">
          <div className="tro-search-wrap">
            <div className="tro-search-icon"><SearchIcon /></div>
            <h1 className="tro-search-title">Track your order</h1>
            <p className="tro-search-subtitle">
              Enter your order ID and the email address you used at checkout to see the latest status.
            </p>

            <form className="tro-form" onSubmit={handleSubmit}>
              <label className="tro-label" htmlFor="tro-order-id">Order ID</label>
              <input
                id="tro-order-id"
                className="tro-input"
                type="text"
                placeholder="e.g. NQ-2025-0001"
                value={orderId}
                onChange={e => setOrderId(e.target.value)}
                autoComplete="off"
              />

              <label className="tro-label" htmlFor="tro-email">Email address</label>
              <input
                id="tro-email"
                className="tro-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />

              {error && <p className="tro-error">{error}</p>}

              <button className="tro-btn tro-btn--primary" type="submit" disabled={loading}>
                {loading ? 'Searching…' : 'Track order'}
              </button>
            </form>

            <p className="tro-search-footnote">
              Have an account? <Link to="/account">Sign in</Link> to see all your orders in one place.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Result view ── */
  const {
    orderId: id, items = [], subtotal = 0, discount = 0, shipping = 0, codFee = 0,
    finalTotal = 0, promoCode, address = {}, deliveryCoords, paymentStatus,
    createdAt, updatedAt, status, tracking,
  } = order;

  const placedOn = createdAt
    ? new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const deliveredOn = status === 'delivered' && updatedAt
    ? new Date(updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const paymentLabel = getPaymentLabel(order);
  const paymentReference = getPaymentReferenceId(order);
  const shippingMethodDescription = getShippingMethodDescription(order);

  const fullName = `${address.firstName || ''} ${address.lastName || ''}`.trim();
  const hasCoords = deliveryCoords && deliveryCoords.lat != null && deliveryCoords.lng != null;

  return (
    <div className="tro-page-outer">
      <main className="tro-main">
        <button className="tro-back" onClick={handleTrackAnother}>
          Track a different order
        </button>

        <div className="tro-inner">
          {/* ── Left column ── */}
          <div className="tro-left">
            <div className="tro-hero">
              <div className={`tro-hero__check${status === 'cancelled' ? ' tro-hero__check--cancelled' : ''}`}>
                {status === 'cancelled' ? <XCircleIcon /> : <CheckCircleIcon />}
              </div>
              <p className="tro-hero__conf">Order #{id}</p>
              <h1 className="tro-hero__title">
                {status === 'cancelled' ? 'Order cancelled' : `Hi${fullName ? `, ${fullName.split(' ')[0]}` : ''}`}
              </h1>
              {placedOn && <p className="tro-hero__date">Placed on {placedOn}{deliveredOn ? ` · Delivered on ${deliveredOn}` : ''}</p>}
            </div>

            {/* ── Status timeline ── */}
            <section className="tro-card">
              <h2 className="tro-card__title">Order status</h2>
              <StatusTimeline status={status} deliveredOn={deliveredOn} />
              {tracking && status !== 'cancelled' && (
                <div className="tro-tracking">
                  <span className="tro-pill"><TruckIcon /> {shippingMethodDescription}</span>
                  <button className="tro-tracking__copy" onClick={handleCopyTracking}>
                    <span className="tro-tracking__num">{tracking}</span>
                    <CopyIcon /> {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </section>

            {/* ── Delivery location map ── */}
            {hasCoords && (
              <section className="tro-card">
                <h2 className="tro-card__title">Delivery location</h2>
                <p className="tro-card__sub">Your plant will arrive at the pinned location below.</p>
                <div className="tro-map-wrap">
                  <MapPreview lat={deliveryCoords.lat} lng={deliveryCoords.lng} />
                </div>
                <p className="tro-map-address">
                  <PinIcon /> {address.address}{address.apartment ? `, ${address.apartment}` : ''}, {address.city}, {address.state} {address.pincode}
                </p>
              </section>
            )}

            {/* ── Order details ── */}
            <section className="tro-card">
              <h2 className="tro-card__title">Order details</h2>
              <div className="tro-details-grid">
                <div className="tro-details-col">
                  <h3 className="tro-details-label">Contact information</h3>
                  <p className="tro-details-text">{address.email || address.phone}</p>

                  <h3 className="tro-details-label">Shipping address</h3>
                  <p className="tro-details-text">
                    {fullName}<br />
                    {address.address}{address.apartment ? `, ${address.apartment}` : ''}<br />
                    {address.city}, {address.state} {address.pincode}<br />
                    India<br />
                    {address.phone}
                  </p>

                  <h3 className="tro-details-label">Shipping method</h3>
                  <p className="tro-details-text">{shippingMethodDescription}</p>
                </div>
                <div className="tro-details-col">
                  <h3 className="tro-details-label">Payment method</h3>
                  <p className="tro-details-text">
                    {paymentLabel}
                    {paymentStatus && (
                      <span className={`tro-pay-status tro-pay-status--${paymentStatus}`}>
                        {paymentStatus === 'paid' ? 'Paid' : paymentStatus === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    )}
                  </p>
                  {paymentReference && (
                    <>
                      <h3 className="tro-details-label">Payment reference</h3>
                      <p className="tro-details-text tro-details-text--mono">{paymentReference}</p>
                    </>
                  )}
                </div>
              </div>
            </section>

            <div className="tro-actions">
              <Link to="/collections/plants" className="tro-btn tro-btn--primary">Continue shopping</Link>
            </div>
          </div>

          {/* ── Right column / summary ── */}
          <aside className="tro-right">
            <div className="tro-summary">
              <div className="tro-summary__items">
                {items.map((item, idx) => {
                  const variants = [item.selectedSize, item.selectedPlanter, item.selectedColor].filter(Boolean);
                  const isBundle = item.itemType === 'bundle';
                  return (
                    <div key={idx} className="tro-summary__item">
                      <div className="tro-summary__img-wrap">
                        <span className="tro-summary__badge">{item.qty}</span>
                        <div className="tro-summary__img">
                          {item.image
                            ? <img src={item.image} alt={item.name}
                                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                            : null}
                          <div className="tro-summary__img-fallback" style={item.image ? { display: 'none' } : {}}>🌿</div>
                        </div>
                      </div>
                      <div className="tro-summary__item-body">
                        <p className="tro-summary__item-name">
                          {item.name}
                          {isBundle && <span className="tro-bundle-badge">Bundle</span>}
                        </p>
                        {variants.length > 0 && (
                          <p className="tro-summary__item-variants">{variants.join(' · ')}</p>
                        )}
                        {/* Bundle: show individual products as proper item rows */}
                        {isBundle && item.bundleItems && item.bundleItems.length > 0 && (
                          <div className="tro-bundle-plants">
                            <p className="tro-bundle-plants__label">Includes:</p>
                            {item.bundleItems.map((bi, i) => (
                              <div key={i} className="tro-bundle-plant">
                                <div className="tro-bundle-plant__img-wrap">
                                  {bi.image
                                    ? <img src={bi.image} alt={bi.name} className="tro-bundle-plant__img"
                                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                    : null}
                                  <div className="tro-bundle-plant__fallback" style={bi.image ? { display: 'none' } : {}}>🌿</div>
                                </div>
                                <span className="tro-bundle-plant__name">{bi.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="tro-summary__item-price">₹{(item.price * item.qty).toLocaleString()}</p>
                    </div>
                  );
                })}
              </div>

              <div className="tro-divider" />

              <div className="tro-summary__lines">
                <div className="tro-summary__line">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>
                {discount > 0 && (
                  <div className="tro-summary__line tro-summary__line--disc">
                    <span>Discount{promoCode ? ` (${promoCode})` : ''}</span>
                    <span>−₹{discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="tro-summary__line">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? <span className="tro-free">Free</span> : `₹${shipping}`}</span>
                </div>
                {codFee > 0 && (
                  <div className="tro-summary__line">
                    <span>Cash on Delivery fee</span>
                    <span>₹{codFee.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="tro-divider" />

              <div className="tro-summary__total">
                <div>
                  <span className="tro-summary__total-label">Total</span>
                  <span className="tro-summary__currency">INR</span>
                </div>
                <span className="tro-summary__total-amount">₹{finalTotal.toLocaleString()}</span>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}